import { EmptyState } from "@ai-teach/ui-web";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { z } from "zod";
import { MainSidebar } from "../../../components/sidebars/MainSidebar";
import { useAuth } from "../../../contexts/AuthContext";
import { FilterPanel } from "./-FilterPanel";
import { LoadingSkeleton } from "./-LoadingSkeleton";
import { TabNavigation } from "./-TabNavigation";

// Lazy load tab components for better performance

// Student tabs
const StudentDashboard = lazy(() =>
  import("./students/-StudentDashboard").then((m) => ({ default: m.StudentDashboard }))
);
const StudentProgress = lazy(() =>
  import("./students/-StudentProgress").then((m) => ({ default: m.StudentProgress }))
);
const StudentGoals = lazy(() =>
  import("./students/-StudentGoals").then((m) => ({ default: m.StudentGoals }))
);

// Teacher tabs
const TeacherDashboard = lazy(() =>
  import("./teachers/-TeacherDashboard").then((m) => ({ default: m.TeacherDashboard }))
);
const TeacherStudents = lazy(() =>
  import("./teachers/-TeacherStudents").then((m) => ({ default: m.TeacherStudents }))
);
const TeacherInterventions = lazy(() =>
  import("./teachers/-TeacherInterventions").then((m) => ({ default: m.TeacherInterventions }))
);

// Admin tabs
const AdminSystemDashboard = lazy(() =>
  import("./admin/-AdminSystemDashboard").then((m) => ({ default: m.AdminSystemDashboard }))
);
const AdminSchoolsDashboard = lazy(() =>
  import("./admin/-AdminSchoolsDashboard").then((m) => ({ default: m.AdminSchoolsDashboard }))
);
const AdminFSRSMonitor = lazy(() =>
  import("./admin/-AdminFSRSMonitor").then((m) => ({ default: m.AdminFSRSMonitor }))
);
const AdminConversationLatency = lazy(() =>
  import("./admin/-AdminConversationLatency").then((m) => ({
    default: m.AdminConversationLatency,
  }))
);

// URL search params schema
const analyticsSearchSchema = z.object({
  school: z.string().optional(),
  teacher: z.string().optional(),
  student: z.string().optional(),
  lesson: z.string().optional(),
  tab: z
    .enum([
      "students",
      "student-progress",
      "student-goals",
      "teacher-dashboard",
      "teacher-students",
      "teacher-interventions",
      "admin-system",
      "admin-schools",
      "admin-fsrs-monitor",
      "admin-conversation-latency",
    ])
    .optional()
    .default("students"),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

export const Route = createFileRoute("/admin/analytics/")({
  validateSearch: analyticsSearchSchema,
  component: AnalyticsDashboard,
});

function AnalyticsDashboard() {
  // Read filters directly from URL - single source of truth
  const filters = useSearch({ from: "/admin/analytics/" });
  const { user } = useAuth();

  // Determine which data to show based on filter hierarchy:
  // 1. If student selected: show that specific student's data
  // 2. If teacher selected (but no student): show aggregated data for all students of that teacher
  // 3. If school selected (but no teacher/student): show aggregated data for all students in school
  // 4. If nothing selected: show current user's data (for "My Dashboard/Progress/Goals" tabs) or aggregated data

  // When viewing "My" tabs (student tabs with no filters), use current user's ID
  const studentId = filters.student || user?.id || "";
  const teacherId = !filters.student && filters.teacher ? filters.teacher : "";
  const schoolId = !filters.student && !filters.teacher && filters.school ? filters.school : "";

  if (!user) {
    return (
      <div className="flex h-screen">
        <MainSidebar />
        <div className="flex-1 overflow-auto p-6">
          <EmptyState title="Not Authenticated" description="Please log in to view analytics" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <MainSidebar />
      <div className="flex-1 overflow-auto p-6">
        <h1 className="h3 mb-6">FSRS Analytics Dashboard</h1>

        <FilterPanel />

        <TabNavigation />

        {/* Tab Content - Wrapped in Suspense for lazy loading */}
        <Suspense fallback={<LoadingSkeleton />}>
          {/* Student tabs */}
          {filters.tab === "students" && (
            <StudentDashboard studentId={studentId} schoolId={schoolId} teacherId={teacherId} />
          )}
          {filters.tab === "student-progress" && <StudentProgress studentId={studentId} />}
          {filters.tab === "student-goals" && <StudentGoals studentId={studentId} />}

          {/* Teacher tabs */}
          {filters.tab === "teacher-dashboard" && (
            <TeacherDashboard teacherId={user.id} classId={filters.school} />
          )}
          {filters.tab === "teacher-students" && (
            <TeacherStudents teacherId={user.id} classId={filters.school} />
          )}
          {filters.tab === "teacher-interventions" && (
            <TeacherInterventions teacherId={user.id} classId={filters.school} />
          )}

          {/* Admin tabs */}
          {filters.tab === "admin-system" && <AdminSystemDashboard />}
          {filters.tab === "admin-schools" && <AdminSchoolsDashboard />}
          {filters.tab === "admin-fsrs-monitor" && <AdminFSRSMonitor />}
          {filters.tab === "admin-conversation-latency" && <AdminConversationLatency />}
        </Suspense>
      </div>
    </div>
  );
}
